import { FC, KeyboardEvent } from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import {
	IColumnEditor,
} from '@aleph/shared-components';
import { useQuery } from '@apollo/client';
import { CreativeType, RedditMediaQuery } from 'wb-reddit/graphql/generated';
import mediaQuery from 'wb-reddit/app/routes/superGrid/columnEditors/media/media.graphql';
import { AdMedia } from 'wb-reddit/app/routes/superGrid/columnDefinitions/ad';
import {
	getFileNameExtension,
	supportedImageFormats,
	supportedVideoFormats,
} from 'wb-reddit/app/routes/superGrid/cellRenderers/media';

export function doesMediaMatchType(media: AdMedia, creativeType: CreativeType) {
	if (!media?.mediaUrl && !media?.mediaThumbnail) {
		return false;
	}

	const fileExtension = getFileNameExtension(media?.mediaUrl ?? '');

	switch (creativeType) {
		case CreativeType.Image:
		case CreativeType.Carousel:
			return supportedImageFormats.includes(fileExtension);
		case CreativeType.Video:
			return supportedVideoFormats.includes(fileExtension);
		default:
			return false;
	}
}

const RedditMediaColumnEditor: FC<IColumnEditor<AdMedia>> = (params) => {
	const { value, setValue, editorContext } = params;

	const { data } = useQuery<RedditMediaQuery>(mediaQuery);

	const selectMedia = (media?: AdMedia) => () => {
		setValue(media);
	};

	const isMediaSupported = (
		(creativeType: CreativeType) => (media: AdMedia) => doesMediaMatchType(media, creativeType)
	)(editorContext?.creativeType);

	const mediaItems = data?.media?.items
		?.filter(isMediaSupported);

	const onKeyDown = (e: KeyboardEvent) => {
		switch (e.key) {
			case 'Enter':
				params.api.stopEditing();

				break;
			case 'Escape':
				params.api.stopEditing(true);

				break;
			case 'ArrowLeft':
				{
					const currSelectedIndex = mediaItems?.findIndex(
						media => media?.mediaUrl === value?.mediaUrl,
					) ?? 0;

					const newItem = mediaItems?.[currSelectedIndex > 0 ? currSelectedIndex - 1 : 0];

					if (newItem) {
						selectMedia(newItem)();
					}
				}

				break;
			case 'ArrowRight':
				{
					const currSelectedIndex = mediaItems?.findIndex(
						media => media?.mediaUrl === value?.mediaUrl,
					) ?? 0;

					const newItem = mediaItems?.[currSelectedIndex + 1];

					if (newItem) {
						selectMedia(newItem)();
					}
				}

				break;
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			className="grid grid-cols-3 gap-[8px] p-[8px]"
			onKeyDown={onKeyDown}
		>
			{
				mediaItems?.map((media, idx) => (
					<button
						autoFocus={idx === 0 || value?.mediaUrl === media?.mediaUrl}
						className={
							classNames(`flex outline-0 items-center bg-[#e9e9e9] 
									border-[2px] hover:border-blue cursor-pointer`, {
								'border-blue':  value?.mediaUrl === media?.mediaUrl,
								'border-white': value?.mediaUrl !== media?.mediaUrl,
							})
						}
						key={idx + (value?.mediaUrl ?? '')}
						onClick={selectMedia(media)}
						onDoubleClick={() => params.api.stopEditing()}
					>
						<img
							alt="media"
							className="h-full aspect-square object-cover"
							src={media?.mediaThumbnail ?? ''}
						/>
					</button>
				))
			}
		</motion.div>
	);
};

export default RedditMediaColumnEditor;
